/* eslint-disable max-lines-per-function */

import { brand } from '../../theme/themePrimitives';
import CardWrapper from '../wrapper/card';
import { FieldType } from '../../helper/form_builder/form_builder_iterfaces';
import RenderFormFields from './builder_helper/render_form_fields';
import SelectedFieldPropertyComponent from './builder_helper/selected_field_property';
import { updateCalendarFormJSONThunk } from '../../redux/thunks/calendar';
import { useParams } from 'react-router';
import { useState } from 'react';
import {
    addFormFieldInCalendarBuilder,
    duplicateFormFieldInCalendarBuilder,
    removeFormFieldInCalendarBuilder,
    reorderFormFieldsInCalendarBuilder,
    settingSelectedCalendarFormFieldProperty
} from '../../redux/slices/calendar_form_builder';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { DeleteOutlineOutlined, DragIndicator, FilterNoneOutlined, KeyboardArrowLeft, Search } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { FIELD_TYPES, makeDataForSelection } from '../../helper/form_builder';
import { settingEditEventFormFlag, settingSelectedEvent } from '../../redux/slices/calendar';
import { useDispatch, useSelector } from 'react-redux';

const FormBuilderForCalendar = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { formFields, selectedFormField } = useSelector((state: RootState) => state.calendarformbuilder);
    const { selectedEvent } = useSelector((state: RootState) => state.calendar);
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { selectedCustomer } = useSelector((state: RootState) => state.customer);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [touched, setTouched] = useState<Record<string, unknown>>({});
    const [errors] = useState<Record<string, unknown>>({});

    const backToCalendarHandler = () => {
        dispatch(settingSelectedEvent(null));
        dispatch(settingEditEventFormFlag(false));
    };

    // Filter fields based on search query
    const filteredFields = Object.entries(FIELD_TYPES)
        .map(([category, fields]) => ({
            category,
            fields: fields.filter((field) => field.label.toLowerCase().includes(searchQuery.toLowerCase()))
        }))
        .filter((category) => category.fields.length > 0);

    // Handle Drag End
    const onDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (source.droppableId === 'field-options' && destination.droppableId === 'form-fields') {
            // Find the dragged field
            const [category, type] = draggableId.split('-') as [keyof typeof FIELD_TYPES, string];

            const fieldGroup = FIELD_TYPES[category];

            if (!fieldGroup) return;

            const draggedField = fieldGroup.find((f) => f.type === type);
            if (draggedField) {
                const data = makeDataForSelection(draggedField);
                dispatch(addFormFieldInCalendarBuilder(data));
                // setFormFields([...formFields, data]);
                dispatch(settingSelectedCalendarFormFieldProperty(data));
            }
        } else if (source.droppableId === 'form-fields' && destination.droppableId === 'form-fields') {
            dispatch(reorderFormFieldsInCalendarBuilder({
                sourceIndex: source.index,
                destinationIndex: destination.index
            }));
        }
    };

    const handleAddField = (field: FieldType) => {
        const data = makeDataForSelection(field);
        dispatch(addFormFieldInCalendarBuilder(data));
        // setFormFields([...formFields, data]);
        dispatch(settingSelectedCalendarFormFieldProperty(data));
        // setFormData((prev) => ({ ...prev, [data.id]: '' }));
    };

    const handleBlur = (fieldId: string) => {
        setTouched((prev) => ({ ...prev, [fieldId]: true }));
        // You can add validation logic here if needed
    };

    const removeFormFieldHandler = (e: React.MouseEvent<HTMLButtonElement>, fieldId: string) => {
        e.stopPropagation();
        dispatch(removeFormFieldInCalendarBuilder(fieldId));
    };

    const onSaveFormHandler = async () => {
        try {
            let user_id = 0;
            if (id && currentUser?.role !== 'client') {
                user_id = selectedCustomer?.id || 0;
            } else {
                user_id = currentUser?.id as number;
            }
            await dispatch(updateCalendarFormJSONThunk({
                calendar_id: selectedEvent?.id as number,
                form_json: JSON.stringify({ fields: formFields.fields }),
                user_id
            })).unwrap();
            dispatch(settingEditEventFormFlag(false));
        } catch (error) {
            //
        }
    };
    return (
        <CardWrapper>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="text" color="secondary" onClick={backToCalendarHandler}><KeyboardArrowLeft />Back to Calendar</Button>
                <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">Event Name: {selectedEvent?.event_name}</Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={onSaveFormHandler}>
                    Save Form
                </Button>
            </Box>
            <CardWrapper sx={{ display: 'flex', p: 2, borderRadius: 1, bgcolor: 'hsl(0, 0%, 98%)' }} >
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* Left Sidebar - Field Types */}
                    <Box flex={1} display="flex" minHeight="82vh" maxHeight="82vh" flexDirection="column" overflow="auto" gap={1}>
                        <Box display="none">
                            <Typography variant="h6" gutterBottom>
                                Add Fields
                            </Typography>
                            <TextField
                                id="search-bar"
                                placeholder="Search Fields"
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: <Search />
                                }}
                            />
                        </Box>
                        {/* Droppable Sidebar Container */}
                        <Droppable droppableId="field-options">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    {filteredFields.filter(({ category }) => category !== 'Multiple_Choice').map(({ category, fields }) => (
                                        <Box display="flex" flexDirection="column" bgcolor="white" border="1px solid #ddd" p={2} borderRadius={2} mb={2} gap={1} key={category}>
                                            <Typography variant="h6" gutterBottom>
                                                {category.replace('_', ' ')}
                                            </Typography>
                                            {/* <Grid container spacing={2}> */}
                                            {fields.filter((field) => field.type !== 'rating' && field.type !== 'slider' && field.type !== 'currency' && field.type !== 'percentage').map((field, index) => (
                                                <Draggable
                                                    key={`${category}-${field.type}`}
                                                    draggableId={`${category}-${field.type}`}
                                                    index={index}
                                                >
                                                    {(draggableProvided) => (
                                                        // <Grid item xs={12} sm={6} md={6} lg={6}>
                                                        <Box
                                                            ref={draggableProvided.innerRef}
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap={1}
                                                            width="100%"
                                                            borderRadius={1}
                                                            // minHeight="60px"
                                                            py={1}
                                                            px={2}
                                                            boxSizing="border-box"
                                                            onClick={() => handleAddField(field)}
                                                            color="black"
                                                            bgcolor="#ebebeb"
                                                            {...draggableProvided.draggableProps}
                                                            {...draggableProvided.dragHandleProps}
                                                            sx={{
                                                                'cursor': 'pointer',
                                                                'color': 'rgba(0, 0, 0, 0.6)',
                                                                '&:hover': { backgroundColor: brand[300], color: 'white' }
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center" gap={1}>{field.icon}
                                                                <Typography variant="body2" fontWeight="bold">{field.label}</Typography></Box>
                                                            <DragIndicator sx={{ color: 'lightgray' }} />
                                                        </Box>
                                                        // </Grid>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {/* </Grid> */}
                                        </Box>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </Box>

                    {/* Right Section - Dropped Form Fields */}
                    <Box flex={3} px={2}>
                        <Droppable droppableId="form-fields">
                            {(provided) => (
                                <Box ref={provided.innerRef} {...provided.droppableProps} minHeight="82vh" maxHeight="82vh" display="flex" flexDirection="column" gap={2} overflow="auto">
                                    {formFields?.fields?.length === 0
                                        ? (
                                            <Typography variant="subtitle1" align="center" color="gray">
                                                Drag & Drop fields here to build your form
                                            </Typography>
                                        )
                                        : (
                                            formFields?.fields
                                                ?.map((field, index) => (
                                                    <Draggable key={field.id} draggableId={field.id} index={index}>
                                                        {(draggableProvided) => (
                                                            <Box
                                                                ref={draggableProvided.innerRef}
                                                                {...draggableProvided.draggableProps}
                                                                {...draggableProvided.dragHandleProps}
                                                                onClick={() => dispatch(settingSelectedCalendarFormFieldProperty(field))}
                                                                bgcolor="white"
                                                                sx={{
                                                                    p: 2,
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '8px'
                                                                }}
                                                            >
                                                                {field.type !== 'submit' && <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                                    <Typography variant={field.type === 'heading' ? (field.heading_size as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') : 'body2'} >{field.label}</Typography>
                                                                    {!field.default && <Box display="flex">
                                                                        <IconButton size="small" onClick={() => dispatch(duplicateFormFieldInCalendarBuilder(field.id))}>
                                                                            <FilterNoneOutlined />
                                                                        </IconButton>
                                                                        <IconButton size="small" color="error" onClick={(e) => removeFormFieldHandler(e, field.id)}>
                                                                            <DeleteOutlineOutlined />
                                                                        </IconButton>
                                                                    </Box>}
                                                                </Box>}
                                                                <RenderFormFields
                                                                    field={field}
                                                                    handleBlur={handleBlur}
                                                                    touched={touched}
                                                                    errors={errors}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))
                                        )}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </Box>
                </DragDropContext>
                <Box flex={1} maxHeight="80vh" bgcolor="white" border="1px solid #ddd" p={2} borderRadius={2} height="max-content" overflow="auto">
                    {selectedFormField ? <SelectedFieldPropertyComponent /> : <></>}
                </Box>
            </CardWrapper>
        </CardWrapper>
    );
};

export default FormBuilderForCalendar;
